// src/components/Multiplayer/Tournament/TournamentResults.jsx
// Final standings + prize breakdown for a completed (or in-progress) tournament.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { tournamentService } from '../../../services/tournamentService';
import './Tournament.css';

const SYMBOLS = { USD: '$', INR: '₹', LRD: 'L$', XOF: 'CFA', CFA: 'CFA', LOCAL: '' };

const TournamentResults = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await tournamentService.getStandings(id);
      setTournament(res.tournament || null);
      setStandings(res.standings || []);
    } catch (e) {
      setError(e.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const fmt = (amount, currency) => {
    const sym = SYMBOLS[currency] ?? '';
    const n = parseFloat(amount || 0);
    if (currency === 'XOF' || currency === 'CFA') return `${n.toFixed(0)} ${sym}`;
    return `${sym}${n.toFixed(2)}`;
  };

  const sorted = [...standings].sort((a, b) => {
    const pa = a.position != null ? a.position : Number.MAX_SAFE_INTEGER;
    const pb = b.position != null ? b.position : Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return (a.score || 0) - (b.score || 0);
  });

  if (loading) {
    return <div className="t-container"><div className="t-loading">Loading results...</div></div>;
  }

  if (error) {
    return (
      <div className="t-container">
        <div className="t-error">{error}</div>
        <Link to="/tournaments" className="t-back">← Back to tournaments</Link>
      </div>
    );
  }

  return (
    <div className="t-container">
      <div className="t-header">
        <h1>🏆 {tournament?.name || 'Tournament'} — Results</h1>
        <p>Final standings and prize breakdown</p>
      </div>

      <Link to="/tournaments" className="t-back">← Back to tournaments</Link>

      <div className="t-results-card">
        <table className="t-results-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th>Score</th>
              <th>Prize</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, i) => {
              const isMe = currentUser && (String(s.user_id) === String(currentUser.id));
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '';
              return (
                <tr key={s.id} className={isMe ? 't-row-me' : ''}>
                  <td>{medal || (s.position || i + 1)}</td>
                  <td>{s.username}{isMe ? ' (you)' : ''}</td>
                  <td>{s.score}</td>
                  <td>{fmt(s.prize, tournament?.currency || 'USD')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sorted.length === 0 && <div className="t-empty">No players in this tournament.</div>}
      </div>
    </div>
  );
};

export default TournamentResults;
